import { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import AuthForm from "./AuthForm";
import ResetPasswordDialog from "./ResetPasswordDialog";

interface AuthPromptDialogProps {
  open: boolean;
  onClose: () => void;
  /** Initial auth form mode. Defaults to sign-in. */
  initialMode?: "signin" | "signup";
  /**
   * Shown in the dialog body. Explains *why* the user is being asked to
   * sign in right now — specificity is what makes a gate feel useful
   * rather than punitive ("Sign in to post" beats "Sign in to continue").
   * Optional; falls back to a generic copy.
   */
  reason?: string;
  /**
   * Identifies the feature that triggered the prompt. Goes into GA4
   * params on every auth event fired from this dialog. Defaults to
   * "prompt".
   */
  source?: string;
}

const DEFAULT_REASON =
  "Sign in to submit surf reports, save home spots, and see the community feed.";

export default function AuthPromptDialog({
  open,
  onClose,
  initialMode = "signin",
  reason,
  source = "prompt",
}: AuthPromptDialogProps) {
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState<string>("");

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: "1rem" }}>Sign in to continue</DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", fontSize: "0.85rem", mb: 2 }}
          >
            {reason ?? DEFAULT_REASON}
          </Typography>
          <AuthForm
            initialMode={initialMode}
            source={source}
            onSuccess={onClose}
            onForgotPassword={(email) => {
              setResetEmail(email);
              setResetOpen(true);
            }}
          />
          <Box sx={{ mt: 2, textAlign: "right" }}>
            <Button
              onClick={onClose}
              size="small"
              sx={{ color: "text.secondary", textTransform: "none" }}
            >
              Cancel
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
      <ResetPasswordDialog
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        initialEmail={resetEmail}
        source={source}
      />
    </>
  );
}
